<template>
  <div class="container">
    <canvas ref="canvas"></canvas>
    <div v-if="showHint" class="hint">
      Touch and drag to create cosmic smoke
    </div>
    <div class="controls">
      <div class="time-attack-toggle">
        <label class="switch">
          <input type="checkbox" v-model="timeAttackActive" @change="handleTimeAttackSwitchChange" />
          <span class="slider"></span>
        </label>
        <span class="switch-label">{{ timeAttackActive ? 'Time Attack On' : 'Time Attack Off' }}</span>
      </div>
      <!-- Time Attack Info fades in/out -->
      <transition name="fade">
        <div v-if="timeAttackStartTime" class="time-attack-info">
          <div class="countdown">Time Left: {{ timeAttackTimeLeft }} s</div>
          <div class="score">Score: {{ timeAttackScore }}</div>
          <div class="highscore">High Score: {{ highScore }}</div>
        </div>
      </transition>
      <transition name="fade">
        <div v-if="timeAttackStartTime" class="progress-container">
          <div class="progress-bar" :style="{ width: coverage + '%' }"></div>
          <div class="progress-label">Coverage: {{ coverage.toFixed(1) }}%</div>
        </div>
      </transition>
    </div>
    <!-- Feature Tip Modal (shown only when Time Attack is off) -->
    <transition name="modal">
      <div class="modal-overlay" v-if="showFeatureModal">
        <div class="modal-content">
          <h2>Feature Tip</h2>
          <p>{{ currentTip }}</p>
          <div class="modal-buttons">
            <button @click="neverShowTips">Never show again</button>
            <button @click="closeModalTip">View later</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'SmokePage',
  data() {
    return {
      canvas: null,
      ctx: null,
      hue: 0,
      particles: [],
      touchActive: false,
      showHint: true,
      animationFrame: null,
      audioContext: null,
      lastSoundTime: 0,
      // Gravity Mechanics
      gravityX: 0,
      gravityY: 0,
      // Time Attack Mode properties
      timeAttackActive: false,
      timeAttackDuration: 30, // seconds
      timeAttackStartTime: null,
      timeAttackScore: 0,
      timeAttackInterval: null,
      highScore: 0,
      currentTime: Date.now(),
      // Progress tracking (grid)
      gridCellSize: 50,
      touchedCells: {},
      // Celebration properties
      celebrationActive: false,
      celebrationParticles: [],
      // Modal (Feature Tip) properties
      showFeatureModal: false,
      neverShowModal: false,
      modalContent: [
        "Tip: Tilt your phone to change the particle direction.",
        "Tip: Press harder to create larger particles.",
        "Tip: Explore every inch of the screen for bonus coverage!",
        "Tip: Enjoy the cosmic visuals and let your creativity flow."
      ],
      modalIndex: 0,
      modalInterval: null
    }
  },
  computed: {
    timeAttackTimeLeft() {
      if (!this.timeAttackActive || !this.timeAttackStartTime) {
        return this.timeAttackDuration.toFixed(1);
      }
      let elapsed = (this.currentTime - this.timeAttackStartTime) / 1000;
      let left = Math.max(0, this.timeAttackDuration - elapsed);
      return left.toFixed(1);
    },
    coverage() {
      if (!this.canvas) return 0;
      const cols = Math.ceil(this.canvas.width / this.gridCellSize);
      const rows = Math.ceil(this.canvas.height / this.gridCellSize);
      const totalCells = cols * rows;
      const touchedCount = Object.keys(this.touchedCells).length;
      return (touchedCount / totalCells) * 100;
    },
    currentTip() {
      return this.modalContent[this.modalIndex];
    }
  },
  watch: {
    timeAttackActive(newVal) {
      // When Time Attack is turned off, clear info and schedule modal tips.
      if (!newVal) {
        this.timeAttackStartTime = null;
        this.clearModalTips();
        if (!this.neverShowModal) {
          this.scheduleModalTips();
        }
      } else {
        // When Time Attack is on, hide modal and clear tip intervals.
        this.clearModalTips();
        this.showFeatureModal = false;
      }
    },
    neverShowModal(newVal) {
      localStorage.setItem('neverShowModal', newVal);
    }
  },
  methods: {
    resize() {
      this.canvas.width = window.innerWidth;
      this.canvas.height = window.innerHeight;
    },
    animate() {
      if (this.celebrationActive) return;
      this.ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
      
      this.particles.forEach((particle, index) => {
        particle.speedX += this.gravityX;
        particle.speedY += this.gravityY;
        this.drawParticle(particle);
        if (particle.alpha <= 0) this.particles.splice(index, 1);
      });
      
      this.hue = (this.hue + 1) % 360;
      this.animationFrame = requestAnimationFrame(this.animate);
    },
    createParticle(x, y, pressure = 1) {
      let size = Math.random() * 15 + 5 * pressure;
      let speedX = (Math.random() * 3 - 1.5) * pressure;
      let speedY = (Math.random() * 3 - 1.5) * pressure;
      let color = `hsla(${this.hue}, 100%, 50%, 0.8)`;
      if (Math.random() < (pressure > 1.5 ? 0.05 : 0.02)) {
        color = `hsla(50, 100%, 60%, 1)`;
        size *= 1.5;
      }
      return { x, y, size, speedX, speedY, color, alpha: 1 };
    },
    drawParticle(particle) {
      this.ctx.fillStyle = particle.color;
      this.ctx.beginPath();
      this.ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      this.ctx.fill();
      particle.x += particle.speedX;
      particle.y += particle.speedY;
      particle.alpha -= 0.01;
      particle.size *= 0.97;
    },
    createBubbleSound(frequency) {
      if (!this.audioContext) return;
      const now = this.audioContext.currentTime;
      if (now - this.lastSoundTime < 0.09) return;
  
      const oscillator = this.audioContext.createOscillator();
      oscillator.type = 'triangle';
      oscillator.frequency.setValueAtTime(frequency, now);
      oscillator.frequency.exponentialRampToValueAtTime(frequency * 0.5, now + 0.05);
      oscillator.detune.setValueAtTime((Math.random() - 0.5) * 20, now);
  
      const bufferSize = this.audioContext.sampleRate * 0.05;
      const noiseBuffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
      const noiseData = noiseBuffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        noiseData[i] = Math.random() * 2 - 1;
      }
      const noiseSource = this.audioContext.createBufferSource();
      noiseSource.buffer = noiseBuffer;
      noiseSource.loop = false;
  
      const filter = this.audioContext.createBiquadFilter();
      filter.type = 'highpass';
      filter.frequency.setValueAtTime(frequency / 2, now);
      filter.Q.setValueAtTime(1, now);
  
      const gainNode = this.audioContext.createGain();
      gainNode.gain.setValueAtTime(1, now);
      gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
  
      const merger = this.audioContext.createGain();
      oscillator.connect(merger);
      noiseSource.connect(merger);
      merger.connect(filter);
      filter.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
  
      oscillator.start(now);
      noiseSource.start(now);
      oscillator.stop(now + 0.05);
      noiseSource.stop(now + 0.05);
  
      this.lastSoundTime = now;
    },
    handleDeviceOrientation(e) {
      const factor = 0.05;
      this.gravityX = (e.gamma || 0) / 90 * factor;
      this.gravityY = (e.beta || 0) / 90 * factor;
    },
    markTouched(x, y) {
      const col = Math.floor(x / this.gridCellSize);
      const row = Math.floor(y / this.gridCellSize);
      const key = `${col}_${row}`;
      this.touchedCells[key] = true;
    },
    handleTouchStart(e) {
      this.touchActive = true;
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }
      if (navigator.vibrate) navigator.vibrate([10, 2, 5]);
      
      const x = e.touches[0].clientX;
      const y = e.touches[0].clientY;
      const pressure = e.touches[0].force || 1;
      this.markTouched(x, y);
      this.addParticles(x, y, pressure);
      
      const baseFreq = 200 + Math.random() * 800;
      this.createBubbleSound(baseFreq);
      this.createBubbleSound(baseFreq * 2);
    },
    handleTouchMove(e) {
      if (this.touchActive) {
        const x = e.touches[0].clientX;
        const y = e.touches[0].clientY;
        const intensity = Math.random();
        const pressure = e.touches[0].force || 1;
        this.markTouched(x, y);
        this.addParticles(x, y, pressure);
        if (navigator.vibrate) {
          const vibIntensity = Math.floor(3 + intensity * 5);
          navigator.vibrate(vibIntensity);
        }
        if (Math.random() < 0.3) {
          const freq = 300 + Math.random() * 1500;
          this.createBubbleSound(freq);
        }
      }
    },
    handleTouchEnd() {
      this.touchActive = false;
      this.createBubbleSound(1500);
      this.createBubbleSound(800);
      if (navigator.vibrate) navigator.vibrate([5, 10, 5]);
    },
    addParticles(x, y, pressure = 1) {
      const count = Math.floor(5 * pressure);
      for (let i = 0; i < count; i++) {
        this.particles.push(this.createParticle(x, y, pressure));
      }
      if (this.timeAttackActive) {
        this.timeAttackScore += count;
      }
    },
    handleTimeAttackSwitchChange() {
      if (this.timeAttackActive) {
        this.startTimeAttack();
      } else {
        this.endTimeAttack();
      }
    },
    startTimeAttack() {
      this.timeAttackActive = true;
      this.timeAttackStartTime = Date.now();
      this.currentTime = Date.now();
      this.timeAttackScore = 0;
      this.touchedCells = {};
      this.clearModalTips();
      this.timeAttackInterval = setInterval(() => {
        this.currentTime = Date.now();
        if (this.timeAttackTimeLeft <= 0) {
          this.endTimeAttack();
        }
      }, 100);
      this.animate();
    },
    endTimeAttack() {
      clearInterval(this.timeAttackInterval);
      this.timeAttackInterval = null;
      this.timeAttackActive = false;
      const bonus = Math.floor(this.coverage * 5);
      const totalScore = this.timeAttackScore + bonus;
      if (totalScore > this.highScore) {
        this.highScore = totalScore;
      }
      this.celebrateScore(totalScore, bonus);
    },
    celebrateScore(finalScore, bonus) {
      this.celebrationActive = true;
      this.celebrationParticles = [];
      for (let i = 0; i < 250; i++) {
        this.celebrationParticles.push({
          x: Math.random() * this.canvas.width,
          y: Math.random() * this.canvas.height,
          vx: (Math.random() - 0.5) * 8,
          vy: (Math.random() - 0.5) * 8,
          size: Math.random() * 5 + 2,
          alpha: 1,
          color: `hsl(${Math.random() * 360}, 100%, 60%)`
        });
      }
      const startTime = Date.now();
      const duration = 3000;
      const runCelebration = () => {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.celebrationParticles.forEach(p => {
          p.x += p.vx;
          p.y += p.vy;
          p.alpha -= 0.01;
          this.ctx.fillStyle = p.color;
          this.ctx.globalAlpha = Math.max(p.alpha, 0);
          this.ctx.beginPath();
          this.ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          this.ctx.fill();
        });
        this.ctx.globalAlpha = 1;
        this.ctx.font = 'bold 64px sans-serif';
        this.ctx.textAlign = 'center';
        this.ctx.fillStyle = '#FFD700';
        this.ctx.fillText(`Score: ${finalScore}`, this.canvas.width / 2, this.canvas.height / 2 - 20);
        this.ctx.font = 'bold 32px sans-serif';
        this.ctx.fillStyle = '#00FFFF';
        this.ctx.fillText(`Coverage Bonus: ${bonus}`, this.canvas.width / 2, this.canvas.height / 2 + 30);
  
        if (Date.now() - startTime < duration) {
          requestAnimationFrame(runCelebration);
        } else {
          this.celebrationActive = false;
          this.celebrationParticles = [];
          this.animate();
        }
      };
      runCelebration();
    },
    // Modal Tip Methods
    scheduleModalTips() {
      if (this.neverShowModal) return;
      this.modalInterval = setInterval(() => {
        if (!this.timeAttackActive && !this.showFeatureModal) {
          this.showFeatureModal = true;
        }
      }, 15000);
    },
    clearModalTips() {
      clearInterval(this.modalInterval);
      this.modalInterval = null;
      this.showFeatureModal = false;
    },
    closeModalTip() {
      this.showFeatureModal = false;
    },
    neverShowTips() {
      this.neverShowModal = true;
      this.clearModalTips();
    }
  },
  mounted() {
    const stored = localStorage.getItem('neverShowModal');
    if (stored) {
      this.neverShowModal = stored === 'true';
    }
    this.canvas = this.$refs.canvas;
    this.ctx = this.canvas.getContext('2d');
    this.resize();
    window.addEventListener('resize', this.resize);
    window.addEventListener('deviceorientation', this.handleDeviceOrientation);
    this.animate();
    this.canvas.addEventListener('touchstart', this.handleTouchStart);
    this.canvas.addEventListener('touchmove', this.handleTouchMove);
    this.canvas.addEventListener('touchend', this.handleTouchEnd);
    setTimeout(() => (this.showHint = false), 5000);
    if (!this.timeAttackActive && !this.neverShowModal) {
      this.scheduleModalTips();
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.resize);
    window.removeEventListener('deviceorientation', this.handleDeviceOrientation);
    cancelAnimationFrame(this.animationFrame);
    this.canvas.removeEventListener('touchstart', this.handleTouchStart);
    this.canvas.removeEventListener('touchmove', this.handleTouchMove);
    this.canvas.removeEventListener('touchend', this.handleTouchEnd);
    if (this.audioContext) {
      this.audioContext.close();
    }
    this.clearModalTips();
  }
}
</script>

<style scoped>
/* Container and canvas styling */
.container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #000;
  touch-action: none;
}
canvas {
  position: fixed;
  top: 0;
  left: 0;
}

/* Hint styling */
.hint {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.4);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 18px;
  padding: 10px 20px;
  user-select: none;
  z-index: 1000;
  pointer-events: none;
}

/* Controls styling */
.controls {
  position: fixed;
  top: 10px;
  left: 10px;
  z-index: 1100;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

/* Switch styling */
.switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
}
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 34px;
}
.slider:before {
  position: absolute;
  content: "";
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}
.switch input:checked + .slider {
  background-color: #2196F3;
}
.switch input:checked + .slider:before {
  transform: translateX(26px);
}
.switch-label {
  margin-left: 10px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 16px;
  color: #fff;
}

/* Time Attack Info styling */
.time-attack-info {
  background: rgba(0, 0, 0, 0.6);
  padding: 12px 18px;
  border-radius: 10px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: #fff;
}
.countdown {
  font-size: 22px;
  font-weight: bold;
  margin-bottom: 5px;
}
.score,
.highscore {
  font-size: 18px;
}

/* Progress Bar styling */
.progress-container {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 5px;
  overflow: hidden;
  height: 20px;
  width: 250px;
  margin-top: 10px;
}
.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #00FFFF, #FF00FF);
  transition: width 0.2s ease;
}
.progress-label {
  font-size: 14px;
  color: #fff;
  margin-top: 4px;
  text-align: center;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* Fade transition for info */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Modal transition */
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s;
}
.modal-enter-from, .modal-leave-to {
  opacity: 0;
}

/* Modal styling */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal-content {
  background: #222;
  color: #fff;
  padding: 20px 30px;
  border-radius: 10px;
  text-align: center;
  max-width: 90%;
}
.modal-content h2 {
  margin-bottom: 10px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}
.modal-content p {
  margin-bottom: 20px;
  font-size: 16px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}
.modal-buttons button {
  background: #2196F3;
  border: none;
  padding: 10px 15px;
  margin: 0 5px;
  color: #fff;
  border-radius: 5px;
  cursor: pointer;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  transition: background 0.3s;
}
.modal-buttons button:hover {
  background: #1976D2;
}
</style>
